import { toast } from 'react-toastify';

/**
 * A LinkedIn connection request is preceded by a "View profile" step a day earlier.
 *
 * The server adds that step whenever a builder adds a connection request (mb-server
 * utils/linkedinViewBeforeConnect), because the employment check only sees a prospect's current
 * company when their profile is viewed. This module carries the explanation the builders show
 * and the canvas checks behind it. The canvas checks mirror the server's rule: walking up from a
 * request, waits and LinkedIn steps that send nothing (like, follow) are passed; a View profile
 * step covers it; anything else does not.
 *
 * Canvas edges point from a node to its parent (source = child, target = parent): that is how
 * getNodesEdgesPrefillDataOnEditMode lays a workflow out and how handleConnect reads a drawn edge.
 */

/** Why the step is there. A builder has no single company to name. */
export const VIEW_BEFORE_CONNECT_EXPLANATION = 'We view their LinkedIn profile first to confirm they still work at the company you\'re targeting. If they\'ve left, we skip them automatically instead of sending a connection request.';

/** What a save reports when the server put (or failed to put) the step in front of a request. */
export interface IViewProfileStepResult {
  added: boolean;
  viewNodeId?: number;
  delayNodeId?: number;
  message: string;
}

const CONNECTION_REQUEST_STEP = 'heyreach_send_connection_request';
const VIEW_PROFILE_STEP = 'heyreach_view_profile';
const NO_MESSAGE_LINKEDIN_STEPS = new Set([VIEW_PROFILE_STEP, 'heyreach_like_post', 'heyreach_follow']);

interface IGraphNode {
  id: string;
  data?: {
    action_type?: string;
    custom_action_details?: Record<string, unknown>;
  };
}

interface IGraphEdge {
  source: string;
  target: string;
}

/** The parent of a canvas node (edges run child → parent). */
const parentOf = (nodeId: string, edges: IGraphEdge[]): string | undefined => (
  edges.find((edge) => edge.source === nodeId)?.target
);

/** The children of a canvas node (edges run child → parent). */
const childrenOf = (nodeId: string, edges: IGraphEdge[]): string[] => (
  edges.filter((edge) => edge.target === nodeId).map((edge) => edge.source)
);

/**
 * The LinkedIn step a canvas node performs.
 *
 * @param node - A canvas node
 * @returns The step's action type, or null for anything else
 */
export const linkedInStepOf = (node?: IGraphNode): string | null => {
  if (node?.data?.action_type !== 'heyreach_campaign') return null;
  const steps = node.data.custom_action_details?.steps;
  if (!Array.isArray(steps)) return null;
  const actionType = (steps[0] as { action_type?: unknown } | undefined)?.action_type;
  return typeof actionType === 'string' ? actionType : null;
};

const passesBetweenViewAndRequest = (node?: IGraphNode): boolean => {
  if (node?.data?.action_type === 'delay') return true;
  const step = linkedInStepOf(node);
  return step !== null && NO_MESSAGE_LINKEDIN_STEPS.has(step) && step !== VIEW_PROFILE_STEP;
};

/**
 * Whether a View profile step comes before this node, with only waits, likes and follows
 * between them.
 *
 * @param nodeId - Canvas id of the connection request
 * @param nodes - Canvas nodes
 * @param edges - Canvas edges
 * @returns True when the request is covered
 */
export const hasViewProfileBefore = (
  nodeId: string,
  nodes: IGraphNode[],
  edges: IGraphEdge[],
): boolean => {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const seen = new Set<string>([nodeId]);
  let current = parentOf(nodeId, edges);
  while (current && !seen.has(current)) {
    seen.add(current);
    const node = byId.get(current);
    if (linkedInStepOf(node) === VIEW_PROFILE_STEP) return true;
    if (!passesBetweenViewAndRequest(node)) return false;
    current = parentOf(current, edges);
  }
  return false;
};

/**
 * The connection requests that follow a node with only waits, likes and follows between them —
 * the requests a View profile step in that node's place would cover.
 *
 * @param nodeId - Canvas id of the node
 * @param nodes - Canvas nodes
 * @param edges - Canvas edges
 * @returns The canvas ids of those requests, empty when there are none
 */
export const requestsAfter = (
  nodeId: string,
  nodes: IGraphNode[],
  edges: IGraphEdge[],
): string[] => {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const requests: string[] = [];
  const seen = new Set<string>([nodeId]);
  const queue = childrenOf(nodeId, edges);
  while (queue.length > 0) {
    const id = queue.shift() as string;
    if (!seen.has(id)) {
      seen.add(id);
      const node = byId.get(id);
      if (linkedInStepOf(node) === CONNECTION_REQUEST_STEP) {
        requests.push(id);
      } else if (passesBetweenViewAndRequest(node)) {
        queue.push(...childrenOf(id, edges));
      }
    }
  }
  return requests;
};

/**
 * The connection requests a View profile step covers; empty when the node is not one.
 *
 * @param nodeId - Canvas id of the node
 * @param nodes - Canvas nodes
 * @param edges - Canvas edges
 * @returns The canvas ids of the requests it covers
 */
export const requestsAfterViewProfile = (
  nodeId: string,
  nodes: IGraphNode[],
  edges: IGraphEdge[],
): string[] => {
  const node = nodes.find((candidate) => candidate.id === nodeId);
  return linkedInStepOf(node) === VIEW_PROFILE_STEP ? requestsAfter(nodeId, nodes, edges) : [];
};

/**
 * Whether deleting these nodes leaves a connection request with no View profile step in front of
 * it. Deleting the request along with its view does not, and neither does deleting one of two
 * views when the other still covers the request.
 *
 * @param deletedIds - Canvas ids being deleted
 * @param nodes - Canvas nodes
 * @param edges - Canvas edges
 * @returns True when the deletion leaves a request without a view
 */
export const deletesViewBeforeRequest = (
  deletedIds: string[],
  nodes: IGraphNode[],
  edges: IGraphEdge[],
): boolean => {
  const deleted = new Set(deletedIds);
  const requests = new Set(
    deletedIds
      .flatMap((id) => requestsAfterViewProfile(id, nodes, edges))
      .filter((id) => !deleted.has(id)),
  );
  if (requests.size === 0) return false;

  // The workflow after the deletion: like the server, a deleted node's child moves up to the
  // nearest ancestor that stays.
  const keptParent = (id: string): string | undefined => {
    let parent = parentOf(id, edges);
    const seen = new Set<string>();
    while (parent && deleted.has(parent) && !seen.has(parent)) {
      seen.add(parent);
      parent = parentOf(parent, edges);
    }
    return parent && !deleted.has(parent) ? parent : undefined;
  };
  const remainingNodes = nodes.filter((node) => !deleted.has(node.id));
  const remainingEdges = remainingNodes.flatMap((node) => {
    const parent = keptParent(node.id);
    return parent ? [{ source: node.id, target: parent }] : [];
  });
  return [...requests].some((id) => !hasViewProfileBefore(id, remainingNodes, remainingEdges));
};

/** Shown when the user is about to remove a View profile step a request relies on. */
export const REMOVE_VIEW_PROFILE_WARNING = {
  title: 'Remove the View profile step?',
  description: `Without it, we can't confirm they still work at the company you're targeting before the connection request goes out. ${VIEW_BEFORE_CONNECT_EXPLANATION}`,
  confirmLabel: 'Remove anyway',
  cancelLabel: 'Keep it',
};

/**
 * Tell the user when a save put a View profile step in front of a connection request, or could
 * not.
 *
 * @param result - The `viewProfileStep` a save returned, if any
 */
export const announceViewProfileStep = (result?: IViewProfileStepResult | null): void => {
  if (!result) return;
  if (result.added) {
    toast.info(`Added a View profile step a day before this connection request. ${VIEW_BEFORE_CONNECT_EXPLANATION}`, {
      autoClose: 10000,
    });
    return;
  }
  toast.warning(result.message, { autoClose: false });
};
